import { LoginContentVariant } from '@chiroup/core/types/LoginContent.type';

type Props = {
  variant: LoginContentVariant;
};

const LoginContentPreview: React.FC<Props> = ({ variant }) => {
  const {
    layout,
    image,
    title,
    subtitle,
    content,
    buttonText,
    buttonLink,
    backgroundColor,
    accentColor,
    textCard,
  } = variant;

  const loginFormMock = (
    <div className="flex flex-col justify-center p-4">
      <div className="w-16 h-5 bg-gray-200 rounded mb-3" />
      <div className="text-xs font-bold text-gray-900 mb-0.5">Sign in</div>
      <div className="text-[8px] text-gray-500 mb-2">Or start your 14-day free trial</div>
      <div className="h-5 bg-gray-100 rounded mb-1.5" />
      <div className="h-5 bg-gray-100 rounded mb-2" />
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 border border-gray-300 rounded-sm" />
          <span className="text-[7px] text-gray-500">Remember me</span>
        </div>
        <span className="text-[7px] text-primary-500">Forgot password?</span>
      </div>
      <div className="h-6 bg-primary-500 rounded" />
    </div>
  );

  if (layout === 'none') {
    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        <div className="flex-1 bg-white dark:bg-dark-gray-900 flex items-center justify-center">
          {loginFormMock}
        </div>
      </div>
    );
  }

  if (layout === 'classic') {
    const bg = backgroundColor || '#1a2e12';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Marketing side */}
        <div
          className="flex-1 relative flex flex-col justify-center p-5 text-white"
          style={{
            background: image
              ? `url(${image}) center/cover`
              : `linear-gradient(135deg, ${accent} 0%, ${bg} 100%)`,
          }}
        >
          {image && (
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${accent}, ${bg})`, opacity: 0.8 }}
            />
          )}
          <div
            className={`relative z-10 ${textCard ? 'bg-black/30 backdrop-blur-md rounded-lg p-3' : ''}`}
          >
            <div className="text-sm font-bold mb-1">{title || 'Title'}</div>
            <div className="text-[10px] opacity-70 mb-2">{subtitle || 'Subtitle'}</div>
            <div className="text-[9px] opacity-50 leading-relaxed mb-3 line-clamp-3">
              {content || 'Content...'}
            </div>
            {buttonText && (
              <a
                href={buttonLink || '#'}
                target="_blank"
                rel="noreferrer"
                className="inline-block self-start px-3 py-1 text-[9px] border border-white/30 bg-white/15 rounded hover:bg-white/25 transition-colors"
              >
                {buttonText}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'card') {
    const bg = backgroundColor || '#f5faf0';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900 border-r border-gray-200 dark:border-dark-gray-600">
          {loginFormMock}
        </div>
        {/* Marketing side */}
        <div
          className="flex-1 flex items-center justify-center p-4"
          style={{ backgroundColor: bg }}
        >
          <div className="bg-white dark:bg-dark-gray-700 rounded-xl p-3 shadow-md w-full max-w-[200px]">
            {image ? (
              <img src={image} alt="" className="h-20 w-full object-cover rounded-lg mb-2" />
            ) : (
              <div
                className="h-20 rounded-lg mb-2 flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
              >
                <span className="text-[8px] text-white/60">Upload image</span>
              </div>
            )}
            <div className="text-xs font-bold text-gray-900 dark:text-dark-gray-100 mb-0.5">
              {title || 'Title'}
            </div>
            <div className="text-[9px] mb-1" style={{ color: accent }}>
              {subtitle || 'Subtitle'}
            </div>
            <div className="text-[8px] text-gray-500 leading-relaxed mb-2 line-clamp-2">
              {content || 'Content...'}
            </div>
            {buttonText && (
              <a
                href={buttonLink || '#'}
                target="_blank"
                rel="noreferrer"
                className="inline-block px-2.5 py-1 text-white text-[8px] rounded transition-colors"
                style={{ backgroundColor: accent }}
              >
                {buttonText}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'showcase') {
    const bg = backgroundColor || '#f5faf0';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Marketing side */}
        <div className="flex-1 p-4 flex flex-col" style={{ backgroundColor: bg }}>
          <div className="text-[9px] font-medium mb-1" style={{ color: accent }}>
            {subtitle || 'Subtitle'}
          </div>
          <div className="text-sm font-extrabold text-gray-900 dark:text-dark-gray-100 leading-tight mb-1.5">
            {title || 'Title'}
          </div>
          <div className="text-[8px] text-gray-500 leading-relaxed mb-2 line-clamp-2">
            {content || 'Content...'}
          </div>
          {buttonText && (
            <a
              href={buttonLink || '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-block self-start px-2.5 py-1 text-[8px] rounded transition-colors mb-2"
              style={{
                color: accent,
                borderColor: `${accent}66`,
                borderWidth: 1,
                borderStyle: 'solid',
              }}
            >
              {buttonText}
            </a>
          )}
          <div className="flex-1 min-h-0 mt-auto overflow-hidden">
            {image ? (
              <img src={image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'custom') {
    // Custom HTML is admin-authored content (not user-generated), rendered in an
    // iframe sandbox for isolation. The admin panel is only accessible to
    // authenticated internal staff, so XSS risk is minimal.
    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Custom HTML side */}
        <iframe
          className="flex-1 border-0"
          sandbox="allow-same-origin"
          title="Custom HTML preview"
          srcDoc={`<!DOCTYPE html>
<html><head>
<style>*{margin:0;padding:0;box-sizing:border-box}html,body{height:100%;overflow:hidden}body{display:flex}</style>
</head><body>${variant.customHtml || '<div style="flex:1;display:flex;align-items:center;justify-content:center;background:#f3f4f6;color:#9ca3af;font-size:12px">Custom HTML preview</div>'}</body></html>`}
        />
      </div>
    );
  }

  // full-bleed
  const bg = backgroundColor || '#77BD43';
  const accent = accentColor || '#F49F01';

  return (
    <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
      {/* Login side */}
      <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
      {/* Marketing side */}
      <div
        className="flex-1 relative flex flex-col justify-center items-center p-4 text-white text-center"
        style={{
          background: image
            ? `url(${image}) center/cover`
            : `linear-gradient(160deg, ${bg} 0%, ${accent} 50%, ${bg}88 100%)`,
        }}
      >
        {image && <div className="absolute inset-0 bg-black/35" />}
        <div
          className={`relative z-10 ${textCard ? 'bg-black/30 backdrop-blur-md rounded-lg p-3' : ''}`}
        >
          <div className="text-xs font-bold mb-0.5">{title || 'Title'}</div>
          <div className="text-[9px] opacity-80 mb-1">{subtitle || 'Subtitle'}</div>
          <div className="text-[8px] opacity-60 leading-relaxed mb-2 line-clamp-2">
            {content || 'Content...'}
          </div>
          {buttonText && (
            <a
              href={buttonLink || '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-block px-2.5 py-1 text-[8px] border border-white/30 bg-white/20 rounded hover:bg-white/30 transition-colors"
            >
              {buttonText}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginContentPreview;
